/**
 * useSpeedtestStore — Pinia composition wiring the speedtest engine, the
 * chassis-phase machine, the per-metric result bridge, and the formatter.
 *
 * App-scoped singleton. Submits results at each phase boundary internally
 * via `useApiStore`. The `onEnd` hook (registered via `initialize`) is
 * the "test ended" notification surface for UI consumers.
 *
 * This file is wiring + composition only. Implementation lives in five
 * sibling modules:
 *
 *   - `./types.ts`         shapes (LiveTestData, MetricStates, UnitInfo)
 *   - `./metric-format.ts` `formatUnit`
 *   - `./metric-state.ts`  metricStates + lifecycle helpers
 *   - `./engine.ts`        Speedtest subscription + EMA + lifecycle quartet
 *   - `./phase.ts`         chassis-phase 5-state derived refs
 *   - `./result-bridge.ts` per-metric `UnitInfo` ComputedRefs
 */

import { computed } from "vue";
import { defineStore } from "pinia";

import { isMetricId, type MetricId } from "@src/types/speedtest.ts";

import { useApiStore } from "@src/stores/useApiStore.ts";
import { useVariantStore } from "@src/stores/useVariantStore.ts";

import type {
    LiveTestData,
    MetricStates,
    PhaseCompletePayload,
    SpeedtestStateAmountKind,
    UnitInfo,
} from "./types.ts";
import { createMetricStates } from "./metric-state.ts";
import { formatUnit } from "./metric-format.ts";
import { createEngineMachine } from "./engine.ts";
import { createPhaseMachine } from "./phase.ts";
import { createResultBridge, policyForVariant } from "./result-bridge.ts";
// DEV-only mock-results injector. Referenced exclusively inside the
// `if (import.meta.env.DEV)` guard below, so Vite's production
// constant-fold (`import.meta.env.DEV` → `false`) lets Rollup tree-shake
// this import — and the whole `./dev-mock.ts` module — out of the prod
// bundle. No production code path reaches it.
import { injectMockResults } from "./dev-mock.ts";

// `TestState` lives in `./types.ts` to break the engine ↔ index
// re-export cycle; this barrel is the public path for consumers.
export { TestState } from "./types.ts";
export type {
    LiveTestData,
    MetricStates,
    UnitInfo,
    PhaseCompletePayload,
    SpeedtestStateAmountKind,
} from "./types.ts";

export const useSpeedtestStore = defineStore("speedtest", () => {
    const apiStore = useApiStore();
    const variantStore = useVariantStore();

    /** Single edge origin: build-time override (empty → same-origin relative).
     *  CF Workers resolves `/api/speedtest/*` at the nearest PoP; client never
     *  selects between regional servers. */
    const serverUrl =
        (import.meta.env.VITE_SPEEDTEST_ORIGIN ?? "") + "/api/speedtest/";

    const metricStates: MetricStates = createMetricStates();

    /** Submit cumulative metrics at a phase boundary. The server upserts by
     *  `sessionId` so partial → complete transitions evolve the same row.
     *
     *  Failure path is best-effort: a failed submission must not
     *  interrupt the test run, but `console.warn` logs the failure
     *  with a structured discriminator so the operator console shows
     *  what happened (no silent swallow). */
    function submitPhaseSnapshot(
        snapshot: PhaseCompletePayload,
        status: "partial" | "complete",
    ): void {
        apiStore
            .submitResult({
                status,
                ping: snapshot.ping,
                jitter: snapshot.jitter,
                download: snapshot.download,
                upload: snapshot.upload,
            })
            .catch((err: unknown) => {
                console.warn("[speedtest] phase-snapshot submission failed", {
                    code: "PHASE_SUBMIT_FAILED",
                    status,
                    error: err instanceof Error ? err.message : String(err),
                });
            });
    }

    const engine = createEngineMachine({
        serverUrl,
        metricStates,
        submitPhaseSnapshot,
    });

    const { data, isRunning, results } = engine;

    const phase = createPhaseMachine(
        data,
        () => results.value?.upload.speedMbps,
    );
    // Pinia setup-store proxy auto-unwraps the inner ComputedRef on access.
    // Wrap it back into a ComputedRef so the bridge can read reactively
    // without each consumer touching Pinia's proxy semantics.
    const variantRef = computed(() => variantStore.variant);
    const bridge = createResultBridge(data, results, variantRef);

    function getStateUnitInfo(
        metricId: string,
        stateAmount?: number,
    ): UnitInfo {
        if (!isMetricId(metricId)) return {};
        const id: MetricId = metricId;
        const amount =
            stateAmount ??
            phase.getSpeedtestStateAmount(
                id === "jitter" ? "ping" : id,
                "Amount",
            );
        return formatUnit(id, amount, policyForVariant(variantStore.variant));
    }

    // ── DEV-only mock-results affordance ─────────────────────────────
    //
    // `injectMockResults` drives the store to the terminal COMPLETE state
    // from a representative `SpeedtestResults` WITHOUT a backend run — so
    // audits + sibling lanes can reach the complete screen (badge,
    // "Complete!" headline, per-metric result rows) for visual
    // verification. The `import.meta.env.DEV` guard is statically replaced
    // with `false` under `--mode production`, so Rollup dead-code-
    // eliminates this block AND tree-shakes `./dev-mock.ts` entirely — the
    // action is `undefined` on the prod store and the injector symbol
    // never enters the prod bundle. There is no production code path here.
    //
    // REVERSAL of DDR-AO-T3 (the natural-edge walker dev-action) — AV.W5
    // / HARDENING PROTO-1. The walker existed to dodge a presumed
    // "synchronous-jump deafness": that `injectMockResults`' terminal
    // flip inside App.onMounted left `useCompletionChoreography`'s
    // `isComplete` watcher permanently deaf. The AV re-audit refuted the
    // premise — the synchronous fixture renders the populated COMPLETE
    // card on the live edge (the π-lane "BLANK" was a capture
    // settle-timing artefact). So the walker + its store-action wiring
    // are retired by root; `injectMockResults` is the single fixture the
    // `?mock-results` consumer drives directly.
    const devActions: {
        injectMockResults?: () => void;
    } = {};
    if (import.meta.env.DEV) {
        const deps = {
            results: engine.results,
            progress: engine.progress,
            metricStates,
        };
        devActions.injectMockResults = () => injectMockResults(deps);
    }

    return {
        // Reactive state
        data,
        metricStates,
        isRunning,
        results,

        // Per-metric UnitInfo bridge
        pingResult: bridge.pingResult,
        jitterResult: bridge.jitterResult,
        downloadResult: bridge.downloadResult,
        uploadResult: bridge.uploadResult,
        resultByMetric: bridge.resultByMetric,
        resultFor: bridge.resultFor,

        // Chassis-phase derived
        currentEnginePhase: phase.currentEnginePhase,
        currentPhase: phase.currentPhase,
        phaseLabel: phase.phaseLabel,
        heroValue: phase.heroValue,
        unit: phase.unit,
        isIdle: phase.isIdle,

        // Lifecycle (delegated to engine machine)
        initialize: engine.initialize,
        configure: engine.configure,
        start: engine.start,
        abort: engine.abort,
        restart: engine.restart,
        reset: engine.reset,

        // Helpers for meter/results rendering
        getSpeedtestStateAmount: phase.getSpeedtestStateAmount,
        getSpeedtestStateName: phase.getSpeedtestStateName,
        getStateUnitInfo,
        formatUnit,

        // DEV-only debug action — `injectMockResults` is present only in
        // dev builds (the `devActions` object is empty in production, so
        // the spread contributes nothing and the prod store has no such
        // key). Spread last so the property is undefined-safe on the
        // consumer side (`speedtestStore.injectMockResults?.()`).
        ...devActions,
    };
});
